import React from "react";
import { Box, Select, MenuItem, Typography, IconButton } from "@mui/material";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const ROWS_OPTIONS = [10, 20, 50, 100];

/**
 * Common pagination component for OA Workflow — Figma aligned.
 * Layout: [ Showing X–Y of Z records ] [ page buttons ] [ Rows per page: N ]
 *
 * @param {{
 *   count: number, page: number, rowsPerPage: number,
 *   onPageChange: Function, onRowsPerPageChange: Function
 * }} props
 */
const OAPagination = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    label = "Rows per page:"
}) => {
    const totalPages = Math.ceil(count / rowsPerPage) || 1;
    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min((page + 1) * rowsPerPage, count);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 2,
            py: 1,
            gap: 3,
            backgroundColor: '#fff',
            borderTop: '1px solid #E2E8F0',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px'
        }} data-testid="oa-pagination">
            
            {/* Rows per page selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '12px', fontWeight: 500 }}>
                    {label}
                </Typography>
                <Select
                    value={rowsPerPage}
                    onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
                    variant="standard"
                    disableUnderline
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#1E293B',
                        '& .MuiSelect-select': { pr: '20px !important', py: 0 },
                        '& .MuiSvgIcon-root': { fontSize: 16 }
                    }}
                >
                    {[10, 20, 50, 100].map((val) => (
                        <MenuItem key={val} value={val} sx={{ fontSize: '12px' }}>
                            {val}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {/* Record count summary (compact) */}
            <Typography variant="body2" sx={{ color: '#64748B', fontSize: '12px', fontWeight: 500, minWidth: 'fit-content' }}>
                {from}-{to} of {count}
            </Typography>

            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 0}
                    size="small"
                    sx={{ color: page === 0 ? '#CBD5E1' : '#64748B' }}
                >
                    <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <IconButton
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    size="small"
                    sx={{ color: page >= totalPages - 1 ? '#CBD5E1' : '#64748B' }}
                >
                    <NavigateNextIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

export default OAPagination;

